import AuthPermission from '../../../high-order-components/auth-permission'
import translate from '../../../i18n'
import {
  IPlan,
  IPlanTranslation,
  PlanStatusEnums,
  PlanTypeEnums,
  SubscriptionRenewalModeEnums,
} from '../../../pages/plans-container/share-type'
import { IVersion } from '../../../pages/versions-container/share-type'
import { PermissionsEnum } from '../../../share-types'
import YesOrNoTag from '../../common/YesOrNoTag'
import VersionsList from '../../versions/versions-list/versions-list'
import { Button, Card, Col, Descriptions, Row, Space, Table, Tag } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import { useNavigate } from 'react-router-dom'

interface IProps {
  plan: IPlan
  versions: IVersion[]
}

const getPlanStatusText = (status?: PlanStatusEnums) => {
  switch (status) {
    case PlanStatusEnums.ACTIVE:
      return translate('plan.status.active')
    case PlanStatusEnums.DELETED:
      return translate('plan.status.deleted')
    case PlanStatusEnums.ARCHIVED:
    default:
      return translate('plan.status.archived')
  }
}

const PlanDescriptions = ({ plan, versions }: IProps) => {
  const navigate = useNavigate()
  const isSubscription = plan.planType === PlanTypeEnums.SUBSCRIPTION

  const translationColumns: ColumnsType<IPlanTranslation> = [
    { title: translate('plan.translationLocale'), dataIndex: 'locale', key: 'locale', width: 100 },
    { title: translate('plan.translationName'), dataIndex: 'name', key: 'name', width: 160 },
    {
      title: translate('plan.marketingHeading'),
      dataIndex: 'marketingHeading',
      key: 'marketingHeading',
    },
    {
      title: translate('plan.marketingSubHeading'),
      dataIndex: 'marketingSubHeading',
      key: 'marketingSubHeading',
    },
    {
      title: translate('plan.benefitDescription', { index: 1 }),
      dataIndex: 'appMarketingTagline1',
      key: 'appMarketingTagline1',
    },
    {
      title: translate('plan.benefitDescription', { index: 2 }),
      dataIndex: 'appMarketingTagline2',
      key: 'appMarketingTagline2',
    },
    {
      title: translate('plan.benefitDescription', { index: 3 }),
      dataIndex: 'appMarketingTagline3',
      key: 'appMarketingTagline3',
    },
  ]

  return (
    <Row gutter={[16, 16]}>
      <Col xs={24} xl={12}>
        <Card
          title={translate('plan.detailTitle')}
          extra={
            <AuthPermission permission={PermissionsEnum.PLANS_WRITE}>
              <Button onClick={() => navigate(`/plans/${plan.id}/edit`)} type="primary">
                {translate('edit')}
              </Button>
            </AuthPermission>
          }
        >
          <Descriptions
            styles={{ label: { alignItems: 'center', fontWeight: 600, width: 220 } }}
            column={1}
            bordered
            size="small"
          >
            <Descriptions.Item label="ID">{plan.id}</Descriptions.Item>
            <Descriptions.Item label={translate('name')}>{plan.name}</Descriptions.Item>
            <Descriptions.Item label={translate('plan.externalId')}>
              {plan.externalId}
            </Descriptions.Item>
            <Descriptions.Item label={translate('plan.city')}>{plan.cityId}</Descriptions.Item>
            <Descriptions.Item label={translate('plan.description')}>
              {plan.description}
            </Descriptions.Item>
            <Descriptions.Item label={translate('plan.type')}>
              {translate(`plan.type.${plan.planType}`)}
            </Descriptions.Item>
            <Descriptions.Item label={translate('plan.price')}>
              {plan.price == null ? '' : `¥${plan.price / 100}`}
            </Descriptions.Item>
            <Descriptions.Item label={translate('plan.originalPrice')}>
              {plan.originalPrice == null ? '' : `¥${plan.originalPrice / 100}`}
            </Descriptions.Item>
            <Descriptions.Item label={translate('plan.credits')}>{plan.credits}</Descriptions.Item>
            <Descriptions.Item label={translate('plan.validityPeriod')}>
              {isSubscription
                ? `${plan.period || 1} ${plan.periodUnit || 'MONTH'}`
                : plan.validityPeriod}
            </Descriptions.Item>
            {isSubscription && (
              <Descriptions.Item label={translate('plan.subscriptionRenewalMode')}>
                {plan.subscriptionRenewalMode
                  ? translate(`plan.subscriptionRenewalMode.${plan.subscriptionRenewalMode}`)
                  : ''}
              </Descriptions.Item>
            )}
            {isSubscription &&
              plan.subscriptionRenewalMode === SubscriptionRenewalModeEnums.AUTO_RENEW && (
                <Descriptions.Item label={translate('plan.wechatContractPlanId')}>
                  {plan.wechatContractPlanId}
                </Descriptions.Item>
              )}
            <Descriptions.Item label={translate('plan.miniProgramSelectable')}>
              <YesOrNoTag value={plan.miniProgramSelectable || false} />
            </Descriptions.Item>
            {!isSubscription && (
              <Descriptions.Item label={translate('plan.isTopUp')}>
                <YesOrNoTag value={plan.isTopUp} />
              </Descriptions.Item>
            )}
            <Descriptions.Item label={translate('plan.isNewUserOnly')}>
              <YesOrNoTag value={plan.isNewUserOnly} />
            </Descriptions.Item>
            <Descriptions.Item label={translate('plan.marketingHeadingBgColor')}>
              {plan.marketingHeadingBgColor}
            </Descriptions.Item>
            <Descriptions.Item label={translate('plan.testSide')}>
              {plan.testSide}
            </Descriptions.Item>
            <Descriptions.Item label={translate('plan.status')}>
              <Tag color={plan.status === PlanStatusEnums.ACTIVE ? 'green' : 'default'}>
                {getPlanStatusText(plan.status)}
              </Tag>
            </Descriptions.Item>
          </Descriptions>
        </Card>
      </Col>
      <Col xs={24} xl={12}>
        <Space orientation="vertical" size={16} style={{ width: '100%' }}>
          <Card title={translate('plan.translations')}>
            <Table
              rowKey={record => record.id || record.locale}
              columns={translationColumns}
              dataSource={plan.translations}
              pagination={false}
              scroll={{ x: true }}
              size="small"
            />
          </Card>
          <Card title={translate('version.title')}>
            <VersionsList
              versions={versions}
              getVersionUrl={version => `/versions/${version.id}`}
            />
          </Card>
        </Space>
      </Col>
    </Row>
  )
}

export default PlanDescriptions
